/**
 * Geometry for the BMP map camera (Jim 2026-10-14): the guide frame drawn over the live
 * view, and the matching crop in the camera's own pixels.
 */

/** Width ÷ height of the guide: between letter (1.29) and 11×17 (1.55) sheets. */
export const GUIDE_ASPECT = 1.42;
/** The guide fills this share of the view's width (or height, whichever binds). */
export const GUIDE_FILL = 0.88;

export type Rect = { x: number; y: number; w: number; h: number };

/** The guide rectangle in view (CSS) pixels, centred. */
export function guideRect(viewW: number, viewH: number): Rect {
  let w = viewW * GUIDE_FILL;
  let h = w / GUIDE_ASPECT;
  if (h > viewH * GUIDE_FILL) {
    h = viewH * GUIDE_FILL;
    w = h * GUIDE_ASPECT;
  }
  return { x: (viewW - w) / 2, y: (viewH - h) / 2, w, h };
}

/**
 * Map a rectangle on screen to the video frame's pixels. The video is shown with
 * object-fit: cover, so it is scaled to fill the view and the overflow is cut off
 * equally on both sides.
 */
export function viewRectToVideo(
  rect: Rect,
  viewW: number,
  viewH: number,
  videoW: number,
  videoH: number
): Rect {
  const scale = Math.max(viewW / videoW, viewH / videoH);
  const shownW = videoW * scale;
  const shownH = videoH * scale;
  const offX = (shownW - viewW) / 2;
  const offY = (shownH - viewH) / 2;
  const x = Math.max(0, (rect.x + offX) / scale);
  const y = Math.max(0, (rect.y + offY) / scale);
  return {
    x,
    y,
    w: Math.min(videoW - x, rect.w / scale),
    h: Math.min(videoH - y, rect.h / scale),
  };
}

export function isPortrait(viewW: number, viewH: number): boolean {
  return viewH > viewW;
}
